import React from 'react';
import { api } from '../api.js';
import { Empty, Loading, Modal, PH, Panel } from './components.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';
import * as T from '../time.js';

// principals-admin.jsx — 공용 검색 권한 원천(계획 문서 5.10 S5): 팀·채널 소속 동기화 상태, 서명 키, 서비스별 작업 PST 정책, 관리자 허가, 범위 위임.
// 시스템 관리자만(서버가 거른다). 바꾸는 것은 모두 감사에 남는다.
const { useState, useEffect } = React;

const SYNC = { ok: ['정상', 'ok'], partial: ['일부 실패', 'warn'], failed: ['실패', 'crit'], not_consented: ['권한 동의 필요', 'warn'], running: ['진행 중', 'info'] };
const KINDS = [{ v: 'group', l: '팀' }, { v: 'channel', l: '비공개·공유 채널' }, { v: 'all:active', l: '회사 전체(재직자)' }];
const END = { replaced: '새 위임으로 바뀜', revoked: '서비스가 끝냄', granter_left: '위임자가 범위를 떠남', granter_inactive: '위임자 퇴사·비활성', service_inactive: '서비스 중지' };

export function PrincipalsAdmin({ onToast }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [permit, setPermit] = useState(null);
  const load = () => api.get('/api/v1/admin/principals').then(x => { setD(x); setErr(null); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);
  if (err) return <Empty icon="shield" title="검색 권한을 볼 수 없습니다" sub={err} />;
  if (!d) return <Loading title="불러오는 중" />;
  const [sl, st] = d.sync ? (SYNC[d.sync.result] ?? [d.sync.result, 'neutral']) : ['아직 없음', 'neutral'];
  const active = d.keys.find(k => k.state === 'active');
  const savePolicy = (s, kinds, allow) => api.put(`/api/v1/admin/principals/policy/${s.id}`, { scope_kinds: kinds, allow_admin_permit: allow })
    .then(() => { onToast(`${s.slug} 정책을 저장했습니다`); load(); }).catch(e => onToast(e.message));
  const revoke = id => api.del(`/api/v1/admin/principals/permits/${id}`).then(() => { onToast('허가를 껐습니다'); load(); }).catch(e => onToast(e.message));
  return (
    <div data-principals-admin style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel>
        <PH title="팀·채널 소속" sub="Microsoft에서 15분마다 받아 둡니다. 2시간 넘게 받지 못한 팀은 검색 권한에서 빠집니다" right={<Button size="sm" variant="secondary" onClick={load}>새로고침</Button>} />
        <div style={{ padding: 14, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span>마지막 동기화: <Pill tone={st} dot>{sl}</Pill> {d.sync && <span className="mono">{T.dateTime(d.sync.started_at).slice(5)} · 팀 {d.sync.teams_ok}/{d.sync.teams_total}{d.sync.teams_failed ? ` · 실패 ${d.sync.teams_failed}` : ''}</span>}</span>
          <span>팀 {d.teams.total}개{d.teams.stale ? <> · <Pill tone="warn" dot>낡은 팀 {d.teams.stale}</Pill></> : ''}</span>
          <span>서명 키: {active ? <span className="mono">{active.kid.slice(0, 8)} · {T.dateTime(active.activated_at).slice(0, 10)} 활성</span> : <Pill tone="warn" dot>없음(프로비저너가 만듭니다)</Pill>}</span>
          <span>관리자 전체 열람 역할: <span className="mono">{d.adminRoles.join(', ')}</span></span>
        </div>
        {d.sync?.error && <div style={{ padding: '0 14px 14px', fontSize: 12, color: 'var(--ink-3)' }}>{d.sync.error}</div>}
      </Panel>
      <Panel>
        <PH title="서비스별 작업 권한" sub="사용자 없이 도는 작업이 받을 수 있는 범위 — 그 범위의 구성원이 위임한 것, 또는 관리자 허가" />
        {d.services.length === 0 ? <Empty icon="box" title="운영 중인 서비스가 없습니다" /> :
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {d.services.map(s => <PolicyRow key={s.id} svc={s} onSave={savePolicy} onPermit={() => setPermit(s)} />)}
          </div>}
      </Panel>
      <Panel scroll>
        <PH title="관리자 허가" sub="최근 30일 — 허가가 살아 있는 동안 그 서비스가 회사 문서 전체를 검색합니다(메일 기록·그룹웨어는 아님)" />
        {d.permits.length === 0 ? <Empty icon="shield" title="허가가 없습니다" /> :
          <DataTable columns={[
            { key: 'service', header: '서비스', width: 110, mono: true },
            { key: 'purpose', header: '목적' },
            { key: 'created_by', header: '만든 사람', width: 100 },
            { key: 'expires_at', header: '끝나는 때', width: 130, mono: true, render: p => T.dateTime(p.expires_at).slice(5) },
            { key: 'state', header: '상태', width: 90, render: p => p.revoked_at ? <Pill tone="neutral" dot>꺼짐</Pill> : new Date(p.expires_at) > new Date() ? <Pill tone="ok" dot>살아 있음</Pill> : <Pill tone="neutral" dot>끝남</Pill> },
            { key: 'act', header: '', width: 70, render: p => !p.revoked_at && new Date(p.expires_at) > new Date() && <Button size="sm" variant="secondary" onClick={() => revoke(p.id)}>끄기</Button> }
          ]} rows={d.permits} />}
      </Panel>
      <Panel scroll>
        <PH title="범위 위임" sub="최근 200건 — 서비스 안의 대상(예: 위키 페이지)에 범위 구성원이 맡긴 범위" />
        {d.grants.length === 0 ? <Empty icon="link" title="위임이 없습니다" /> :
          <DataTable columns={[
            { key: 'service', header: '서비스', width: 110, mono: true },
            { key: 'resource', header: '대상', mono: true },
            { key: 'scope', header: '범위', mono: true },
            { key: 'granted_by', header: '위임자', width: 100 },
            { key: 'granted_at', header: '위임', width: 120, mono: true, render: g => T.dateTime(g.granted_at).slice(5) },
            { key: 'ended', header: '상태', width: 140, render: g => g.ended_at ? <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{END[g.end_reason] ?? g.end_reason}</span> : <Pill tone="ok" dot>살아 있음</Pill> }
          ]} rows={d.grants} />}
      </Panel>
      {permit && <PermitModal svc={permit} onClose={() => setPermit(null)} onToast={onToast} onDone={() => { setPermit(null); load(); }} />}
    </div>
  );
}

function PolicyRow({ svc, onSave, onPermit }) {
  const [kinds, setKinds] = useState(svc.scope_kinds);
  const [allow, setAllow] = useState(svc.allow_admin_permit);
  const dirty = JSON.stringify([...kinds].sort()) !== JSON.stringify([...svc.scope_kinds].sort()) || allow !== svc.allow_admin_permit;
  const toggle = v => setKinds(k => k.includes(v) ? k.filter(x => x !== v) : [...k, v]);
  return (
    <div data-principal-policy={svc.slug} style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
      <span className="mono" style={{ width: 110 }}>{svc.slug}</span>
      {KINDS.map(k => <Checkbox key={k.v} size="sm" label={`위임: ${k.l}`} checked={kinds.includes(k.v)} onChange={() => toggle(k.v)} />)}
      <Checkbox size="sm" label="관리자 허가" checked={allow} onChange={() => setAllow(a => !a)} />
      <div style={{ flex: 1 }} />
      <Button size="sm" variant="secondary" disabled={!dirty} onClick={() => onSave(svc, kinds, allow)}>저장</Button>
      <Button size="sm" variant="secondary" disabled={!svc.allow_admin_permit} onClick={onPermit}>허가 만들기</Button>
    </div>
  );
}

function PermitModal({ svc, onClose, onToast, onDone }) {
  const [purpose, setPurpose] = useState('');
  const [hours, setHours] = useState('4');
  const ok = purpose.trim() && /^\d+$/.test(hours) && +hours >= 1 && +hours <= 24;
  const save = () => api.post('/api/v1/admin/principals/permits', { service_id: svc.id, purpose: purpose.trim(), hours: +hours })
    .then(() => { onToast('허가를 만들었습니다'); onDone(); }).catch(e => onToast(e.message));
  return (
    <Modal width={460} title={`${svc.slug} — 관리자 허가`} icon="shield" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
      <Button size="sm" disabled={!ok} onClick={save}>만들기</Button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>허가가 살아 있는 동안 이 서비스는 팀·채널과 관계없이 회사 문서 전체를 검색합니다. 품질 비교·배포 검증·구축 시험에만 쓰세요.</div>
        <Field label="목적" value={purpose} maxLength={200} onChange={e => setPurpose(e.target.value)} />
        <Field label="기간(시간, 1~24)" value={hours} maxLength={2} onChange={e => setHours(e.target.value)} />
      </div>
    </Modal>
  );
}
